import type { TenantSlug } from "@/lib/theme-presets";

/**
 * The A v2.2 package's instructor fixtures (scenes/instructor.js): Murtada,
 * Session 5, from the PREWALK records at bfad2580; Mufid, Session 9, with the
 * nine learners of migrations/008_seed_mufid_content.sql. Specimen data only.
 *
 * C3: every refused and not-confirmed sentence in the specimen is a
 * placeholder for the server's own sentence, which the product renders as
 * plain text. The marker lives here, never in visible copy (CASE-025).
 */
export interface InstructorFixture {
  session: string;
  date: string;
  task: string;
  due?: string;
  learners: string[];
  prepare: string;
  /** What learners read before the session: the Prepare part's one line. */
  before: string;
  outline: string;
  recap: string;
  next: string;
  conflict: string;
  subject: { name: string; short: string; words: number; at: string; text: string; comment: string };
  band: { eyebrow: string; title: string; meta: string };
}

export const INSTRUCTOR: Record<TenantSlug, InstructorFixture> = {
  murtada: {
    session: "Session 5",
    date: "Wed, Oct 21",
    task: "Counsel and evidence",
    learners: ["Maryam Teaching Day", "Zaynab al-Amīnī"],
    prepare: "Before the session and 1 task",
    before: "Read the case and note the competing goods in it.",
    outline: "Outline: Discuss competing goods, 15 of 90 min",
    recap: "We compared the competing goods and tested a provisional counsel against an objection.",
    next: "Hand in your counsel and evidence. Revise your evidence if the feedback asks you to.",
    conflict: "Zaynab al-Amīnī",
    subject: {
      name: "Maryam Teaching Day",
      short: "Maryam",
      words: 47,
      at: "Wed, Oct 21, 12:38 AM",
      text: "My provisional counsel is to disclose the conflict of interest before accepting the gift. The evidence is that disclosure preserves the affected person's informed choice. An objection is that disclosure may embarrass the giver; I would answer that a private explanation can protect dignity while preserving trust.",
      comment: "Please revise: name one specific passage or principle as evidence, then explain why it supports disclosure. Keep your objection and response.",
    },
    band: { eyebrow: "TDAY-101 · Teaching Day: Ethics in Practice", title: "Session 5: Teaching day live seminar", meta: "Wed, Oct 21 · Zoom · 2 learners" },
  },
  mufid: {
    session: "Session 9",
    date: "Wed, Oct 21",
    task: "Compare two rulings",
    due: "due Fri, Oct 23, 8:00 PM",
    learners: ["Baqir Salih", "Zahra Haydari", "Ali Mansuri", "Husayn Rangwala", "Jafar Poonawala", "Sakina Ladha", "Amina Devji", "Taha Nathoo", "Rukayya Bhimji"],
    prepare: "Before the session, 1 reading and 1 task",
    before: "Read the two rulings and explain the evidence supporting each.",
    outline: "Outline: 0 of 90 min planned",
    recap: "We compared two rulings and the evidence used to support each.",
    next: "Hand in the comparison; revise it after feedback.",
    conflict: "Taha Nathoo",
    subject: {
      name: "Baqir Salih",
      short: "Baqir",
      words: 55,
      at: "Wed, Oct 21, 12:38 AM",
      text: "The first ruling follows the apparent meaning of the text. Its evidence is the wording that addresses the case directly. The second ruling relies on a general principle and applies it where the particular wording is silent. I prefer the first ruling for this case because the specific evidence is stronger than a general inference.",
      comment: "Please revise your comparison: quote the specific wording that supports the first ruling, and explain why that wording outweighs the general principle in this case.",
    },
    band: { eyebrow: "USUL-210 · Uṣūl al-Fiqh: al-Rasāʾil · CRN 20620", title: "Session 9: Live seminar", meta: "Wed, Oct 21 · Zoom · 9 learners" },
  },
};

export const INSTRUCTOR_STATES = [
  ["live", "Try it"],
  ["during", "During class"],
  ["closing", "Closing"],
  ["intent", "Your choice comes first"],
  ["intent-blocked", "Your choice, not ready yet"],
  ["refused", "Not done"],
  ["unknown", "Not confirmed"],
  ["partial", "Partly done"],
  ["conflict", "Someone else saved"],
  ["closed", "Just closed"],
  ["roll", "Roll update"],
  ["review", "Review"],
  ["stale", "Not refreshed"],
] as const;

export type InstructorState = (typeof INSTRUCTOR_STATES)[number][0];

export const STATES_GROUPS = [
  ["all", "Everything"],
  ["fact", "Answers"],
  ["list", "Records"],
  ["task", "Changes"],
  ["outcomes", "Results"],
  ["home", "A Home"],
] as const;

export type StatesGroup = (typeof STATES_GROUPS)[number][0];

export type Outcome = "confirmed" | "refused" | "unknown" | "partial";
